import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Result, Spin, Input, Form, message as antdMessage } from 'antd';
import { verifyEmail, resendVerificationEmail } from '../../libs/api';
import Button from '../common/Button';
import ThreeBackground from './ThreeBackground';
import styles from '@/components/Auth/Auth.module.less';
import type { components } from '@/api/types';
import { getErrorMessage } from '@/utils/errorHandler';

type ErrorResponseDto = components['schemas']['ErrorResponseDto'];
type RateLimitErrorDto = components['schemas']['RateLimitErrorDto'];

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifyStatus, setVerifyStatus] = useState<'success' | 'error' | 'expired' | 'already'>(
    'success',
  );
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [retryAfter, setRetryAfter] = useState<number>(0);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setVerifyStatus('error');
      setMessage('验证链接无效，请检查您的邮箱或重新注册。');
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        const result = await verifyEmail(token);
        setVerifyStatus('success');
        setMessage(`邮箱 ${result.email} 验证成功！即将跳转到登录页面...`);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error: unknown) {
        const errorData = (error as { details?: unknown })?.details as ErrorResponseDto | undefined;
        if (errorData?.error === 'VERIFICATION_EXPIRED') {
          setVerifyStatus('expired');
          setMessage('验证链接已过期，请重新发送验证邮件。');
          setShowEmailInput(true);
        } else if (errorData?.error === 'ALREADY_VERIFIED') {
          setVerifyStatus('already');
          setMessage('该邮箱已经验证过，您可以直接登录。');
        } else if (errorData?.error === 'INVALID_TOKEN') {
          setVerifyStatus('error');
          setMessage('验证链接无效，请检查您的邮箱或重新注册。');
        } else {
          setVerifyStatus('error');
          setMessage(getErrorMessage(error, '验证失败，请稍后重试。'));
        }
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token, navigate]);

  const handleResend = async () => {
    if (!email || !email.trim()) {
      antdMessage.error('请输入邮箱地址');
      return;
    }

    setResending(true);
    setRetryAfter(0);
    try {
      const result = await resendVerificationEmail(email);
      antdMessage.success(result.message || '验证邮件已重新发送，请查收邮箱。');
      setMessage(`验证邮件已发送至 ${result.email}，请查收邮箱。`);
      setShowEmailInput(false);
    } catch (error: unknown) {
      const errorData = (error as { details?: unknown })?.details as
        | RateLimitErrorDto
        | ErrorResponseDto
        | undefined;
      if (errorData && typeof errorData === 'object' && 'retryAfter' in errorData) {
        setRetryAfter((errorData as RateLimitErrorDto).retryAfter);
        antdMessage.warning(
          `发送频率过高，请等待 ${(errorData as RateLimitErrorDto).retryAfter} 秒后重试`,
        );
      } else {
        antdMessage.error(getErrorMessage(error, '发送失败，请稍后重试。'));
      }
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (retryAfter > 0) {
      const timer = setInterval(() => {
        setRetryAfter(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [retryAfter]);

  if (loading) {
    return (
      <div className={styles['auth-page']}>
        <ThreeBackground />
        <div className={styles['auth-container']}>
          <div
            className={styles['auth-form']}
            style={{ textAlign: 'center', padding: '60px 20px' }}
          >
            <Spin size="large" />
            <p style={{ marginTop: 20, color: '#666' }}>正在验证您的邮箱...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['auth-page']}>
      <ThreeBackground />
      <div className={styles['auth-container']}>
        <div className={styles['auth-form']}>
          <Result
            status={verifyStatus === 'success' || verifyStatus === 'already' ? 'success' : 'error'}
            title={
              verifyStatus === 'success'
                ? '邮箱验证成功'
                : verifyStatus === 'already'
                  ? '邮箱已验证'
                  : '邮箱验证失败'
            }
            subTitle={
              <div>
                <p>{message}</p>
                {showEmailInput && (
                  <div style={{ marginTop: 20, textAlign: 'left' }}>
                    <Form.Item label="请输入您的邮箱地址" style={{ marginBottom: 10 }}>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        size="large"
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            }
            extra={[
              (verifyStatus === 'expired' || showEmailInput) && (
                <Button
                  key="resend"
                  onClick={handleResend}
                  disabled={resending || retryAfter > 0 || !email}
                  className="primaryButton"
                >
                  {resending
                    ? '发送中...'
                    : retryAfter > 0
                      ? `请等待 ${retryAfter} 秒`
                      : '重新发送验证邮件'}
                </Button>
              ),
              verifyStatus === 'error' && !showEmailInput && (
                <Button key="register" className="grayButton" onClick={() => navigate('/register')}>
                  返回注册
                </Button>
              ),
              <Button
                key="login"
                className="primaryButton"
                onClick={() => navigate('/login')}
                style={{ marginTop: 10 }}
              >
                前往登录
              </Button>,
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
